/*
 * NseChartEngine — 6 chart types over lightweight-charts v5.
 * Data model: rawDailyData = [{ time: 'YYYY-MM-DD', open, high, low, close, volume }, ...]
 *
 * Tier gating maps Melian Dialogue's lowercase tiers → engine's uppercase enum.
 */
import { CandlestickSeries, LineSeries, BarSeries, HistogramSeries } from "lightweight-charts";
import { sma, ema, rsi, macd, bollinger } from "@/lib/indicators";


export const TIERS = {
    FREE:     "FREE",
    ADVANCED: "ADVANCED",
    EXPERT:   "EXPERT",
};

const TIER_WEIGHT = { FREE: 1, ADVANCED: 2, EXPERT: 3 };


export const CHART_TYPES = {
    // Aug 2026 · baseline chart types — free on every tier, every tradable
    // instrument (equities, indices, commodities, FX).
    // Sep 2026 · all remaining chart types opened to Obondo Advanced (tier
    // "advanced") — every chart type is now selectable at ADVANCED and up;
    // only the FREE tier is limited to Line/Candlestick/Bar.
    LINE:             { id: "LINE",             name: "Line",               tier: TIERS.FREE,     type: "line" },
    DAILY_CANDLES:    { id: "DAILY_CANDLES",    name: "Candlestick",        tier: TIERS.FREE,     type: "candlestick" },
    BAR:              { id: "BAR",              name: "Bar (OHLC)",        tier: TIERS.FREE,     type: "bar" },
    WEEKLY:           { id: "WEEKLY",           name: "Weekly Candlestick", tier: TIERS.FREE, type: "candlestick" },
    CLOSE_ONLY:       { id: "CLOSE_ONLY",       name: "Close-Only Line",    tier: TIERS.FREE, type: "line" },
    HEIKIN_ASHI:      { id: "HEIKIN_ASHI",      name: "Heikin-Ashi",        tier: TIERS.FREE, type: "candlestick" },
    THREE_LINE_BREAK: { id: "THREE_LINE_BREAK", name: "3-Line Break",       tier: TIERS.FREE, type: "candlestick", nonTimeBased: true },
    RENKO:            { id: "RENKO",            name: "Renko (Close)",      tier: TIERS.FREE, type: "candlestick", nonTimeBased: true },
    POINT_FIGURE:     { id: "POINT_FIGURE",     name: "Point & Figure",     tier: TIERS.FREE, type: "candlestick", nonTimeBased: true },
};


/** Normalise a MelianDialogue tier string (free/advanced/expert) to the engine enum. */
export function normaliseTier(tier) {
    const t = String(tier || "").toLowerCase();
    // Institutional tiers (MoE + CMA) get the full expert unlock — advanced
    // chart types, 365-day history, etc. Feb 26 2026.
    if (t === "expert" || t === "moe_institution" || t === "cma_institution") return TIERS.EXPERT;
    if (t === "advanced") return TIERS.ADVANCED;
    return TIERS.FREE;
}


export class NseChartEngine {
    /** @param {IChartApi} chartInstance created via `createChart(container, opts)` */
    constructor(chartInstance) {
        this.chart = chartInstance;
        this.activeSeries = null;
        this._indicatorSeries = [];
        this._riskLines = [];
    }

    /**
     * Render a chart type. Throws on access denied or unknown type.
     * @param {string} chartTypeId
     * @param {Array}  rawDailyData
     * @param {string} userTier  — may be uppercase enum or lowercase MelianDialogue tier
     * @param {Object} [options] — { brickSize, boxSize, reversalBoxes }
     */
    renderChart(chartTypeId, rawDailyData, userTier, options = {}) {
        let config = CHART_TYPES[chartTypeId];
        if (!config) {
            // Unknown or non-string chart type (e.g. a corrupted localStorage
            // pref) — fall back to LINE instead of throwing.
            chartTypeId = CHART_TYPES.LINE.id;
            config = CHART_TYPES.LINE;
        }

        const tier = normaliseTier(userTier);
        if (!this._hasAccess(tier, config.tier)) {
            throw new Error(`Access Denied: "${config.name}" is only available for Institution users.`);
        }

        // Clear prior series
        if (this.activeSeries) {
            this.chart.removeSeries(this.activeSeries);
            this.activeSeries = null;
            this._riskLines = [];
        }

        this.chart.applyOptions({
            timeScale: {
                timeVisible: !config.nonTimeBased,
                secondsVisible: false,
            },
        });

        const processed = this._transform(chartTypeId, rawDailyData, options);

        if (config.type === "candlestick") {
            this.activeSeries = this.chart.addSeries(CandlestickSeries, {
                upColor: "#22c55e",
                downColor: "#ef4444",
                borderVisible: false,
                wickUpColor: "#22c55e",
                wickDownColor: "#ef4444",
                wickVisible: chartTypeId !== CHART_TYPES.RENKO.id,
            });
        } else if (config.type === "bar") {
            this.activeSeries = this.chart.addSeries(BarSeries, {
                upColor: "#22c55e",
                downColor: "#ef4444",
            });
        } else if (config.type === "line") {
            this.activeSeries = this.chart.addSeries(LineSeries, {
                color: "#006600",
                lineWidth: 2,
            });
        } else {
            throw new Error(`Unsupported chart type "${config.type}".`);
        }
        this.activeSeries.setData(processed);
        this.chart.timeScale().fitContent();
        return { count: processed.length };
    }

    /**
     * setRiskLevels — draw/refresh horizontal guide lines on the primary
     * price series for the trader's entry, stop-loss, and take-profit
     * price levels. Purely cosmetic (`createPriceLine`), so failures never
     * block the underlying chart render.
     * @param {{ entryPrice?: number, stopLossPrice?: number, takeProfitPrice?: number }} levels
     */
    setRiskLevels(levels = {}) {
        this._clearRiskLevels();
        if (!this.activeSeries) return;
        const { entryPrice, stopLossPrice, takeProfitPrice } = levels;
        try {
            if (entryPrice) {
                this._riskLines.push(this.activeSeries.createPriceLine({
                    price: entryPrice, color: "rgba(148,163,184,0.85)", lineWidth: 1,
                    lineStyle: 3, axisLabelVisible: true, title: "Entry",
                }));
            }
            if (stopLossPrice) {
                this._riskLines.push(this.activeSeries.createPriceLine({
                    price: stopLossPrice, color: "#ef4444", lineWidth: 2,
                    lineStyle: 2, axisLabelVisible: true, title: "Stop-loss",
                }));
            }
            if (takeProfitPrice) {
                this._riskLines.push(this.activeSeries.createPriceLine({
                    price: takeProfitPrice, color: "#22c55e", lineWidth: 2,
                    lineStyle: 2, axisLabelVisible: true, title: "Take-profit",
                }));
            }
        } catch (e) { /* risk guide lines are cosmetic — never block render */ }
    }

    _clearRiskLevels() {
        (this._riskLines || []).forEach((line) => {
            try { this.activeSeries?.removePriceLine(line); } catch (e) { /* already gone */ }
        });
        this._riskLines = [];
    }

    /**
     * applyIndicators — overlay MA/EMA/Bollinger on the price pane (pane 0)
     * and RSI/MACD in their own sub-panes below it, using lightweight-charts
     * v5's native multi-pane API. `dailyData` is the SAME raw OHLC array
     * passed to renderChart (indicators always compute off real daily
     * closes, regardless of which chart type is currently displayed).
     * @param {Array} dailyData
     * @param {{ ma?: {enabled,period}, ema?: {enabled,period}, bollinger?: {enabled,period,stdDev}, rsi?: {enabled}, macd?: {enabled} }} config
     */
    applyIndicators(dailyData, config = {}) {
        this._clearIndicators();
        if (!dailyData || dailyData.length === 0) return;
        let nextPane = 1;

        if (config.ma?.enabled) {
            const s = this.chart.addSeries(LineSeries, { color: "#f59e0b", lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false }, 0);
            s.setData(sma(dailyData, config.ma.period || 20));
            this._indicatorSeries.push(s);
        }
        if (config.ema?.enabled) {
            const s = this.chart.addSeries(LineSeries, { color: "#8b5cf6", lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false }, 0);
            s.setData(ema(dailyData, config.ema.period || 20));
            this._indicatorSeries.push(s);
        }
        if (config.bollinger?.enabled) {
            const { upper, mid, lower } = bollinger(dailyData, config.bollinger.period || 20, config.bollinger.stdDev || 2);
            [[upper, "#38bdf8"], [mid, "#94a3b8"], [lower, "#38bdf8"]].forEach(([series, color]) => {
                const s = this.chart.addSeries(LineSeries, { color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false }, 0);
                s.setData(series);
                this._indicatorSeries.push(s);
            });
        }
        if (config.rsi?.enabled) {
            const paneIdx = nextPane++;
            const s = this.chart.addSeries(LineSeries, { color: "#e879f9", lineWidth: 1.5 }, paneIdx);
            s.setData(rsi(dailyData, 14));
            try {
                s.createPriceLine({ price: 70, color: "rgba(239,68,68,0.5)", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "70" });
                s.createPriceLine({ price: 30, color: "rgba(34,197,94,0.5)", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "30" });
            } catch (e) { /* price lines are cosmetic — never block render */ }
            this._indicatorSeries.push(s);
            try { this.chart.panes()[paneIdx]?.setHeight(100); } catch (e) { /* pane sizing is cosmetic */ }
        }
        if (config.macd?.enabled) {
            const paneIdx = nextPane++;
            const { macdLine, signalLine, histogram } = macd(dailyData, 12, 26, 9);
            const hist = this.chart.addSeries(HistogramSeries, { priceFormat: { type: "price", precision: 4, minMove: 0.0001 } }, paneIdx);
            hist.setData(histogram);
            const line1 = this.chart.addSeries(LineSeries, { color: "#38bdf8", lineWidth: 1.5 }, paneIdx);
            line1.setData(macdLine);
            const line2 = this.chart.addSeries(LineSeries, { color: "#f97316", lineWidth: 1.5 }, paneIdx);
            line2.setData(signalLine);
            this._indicatorSeries.push(hist, line1, line2);
            try { this.chart.panes()[paneIdx]?.setHeight(100); } catch (e) { /* pane sizing is cosmetic */ }
        }
    }

    _clearIndicators() {
        (this._indicatorSeries || []).forEach((s) => {
            try { this.chart.removeSeries(s); } catch (e) { /* series already gone */ }
        });
        this._indicatorSeries = [];
        try {
            const panes = this.chart.panes();
            for (let i = panes.length - 1; i >= 1; i--) {
                if (panes[i].getSeries().length === 0) this.chart.removePane(i);
            }
        } catch (e) { /* pane cleanup is best-effort */ }
    }

    destroy() {
        try { this._clearIndicators(); } catch (e) { /* already gone */ }
        try { this._clearRiskLevels(); } catch (e) { /* already gone */ }
        try {
            if (this.activeSeries) this.chart.removeSeries(this.activeSeries);
        } catch (e) { /* series already gone */ }
        this.activeSeries = null;
    }

    /* ── RBAC ──────────────────────────────────────────────── */
    _hasAccess(userTier, requiredTier) {
        return (TIER_WEIGHT[userTier] || 1) >= (TIER_WEIGHT[requiredTier] || 1);
    }

    /* ── Transformation dispatcher ─────────────────────────── */
    _transform(id, data, opts) {
        switch (id) {
            case CHART_TYPES.LINE.id:             return this._convertToCloseOnly(data);
            case CHART_TYPES.DAILY_CANDLES.id:     return this._passThroughOhlc(data);
            case CHART_TYPES.BAR.id:               return this._passThroughOhlc(data);
            case CHART_TYPES.WEEKLY.id:           return this._convertToWeekly(data);
            case CHART_TYPES.CLOSE_ONLY.id:       return this._convertToCloseOnly(data);
            case CHART_TYPES.HEIKIN_ASHI.id:      return this._convertToHeikinAshi(data);
            case CHART_TYPES.THREE_LINE_BREAK.id: return this._convertToThreeLineBreak(data);
            case CHART_TYPES.RENKO.id:            return this._convertToRenko(data, opts.brickSize || 1.0);
            case CHART_TYPES.POINT_FIGURE.id:     return this._convertToPointAndFigure(data, opts.boxSize || 0.5, opts.reversalBoxes || 3);
            default: return [];
        }
    }

    /* ── 0. Plain daily OHLC passthrough (candlestick / bar) ─── */
    _passThroughOhlc(daily) {
        return daily.map((d) => ({ time: d.time, open: d.open, high: d.high, low: d.low, close: d.close }));
    }

    /* ── Fake-time helpers for non-time-based series ───────── */
    /** lightweight-charts accepts a UNIX seconds timestamp as `time` for candle series. */
    _fakeTime(counter) { return counter; }

    /* ── 1. Weekly aggregation ─────────────────────────────── */
    _convertToWeekly(daily) {
        const weekly = [];
        let current = null;
        daily.forEach((day) => {
            const d = new Date(`${day.time}T00:00:00Z`);
            const dow = d.getUTCDay();
            const diff = d.getUTCDate() - dow + (dow === 0 ? -6 : 1);
            const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), diff))
                .toISOString().split("T")[0];
            if (!current || current.time !== monday) {
                if (current) weekly.push(current);
                current = { time: monday, open: day.open, high: day.high, low: day.low, close: day.close };
            } else {
                current.high  = Math.max(current.high, day.high);
                current.low   = Math.min(current.low,  day.low);
                current.close = day.close;
            }
        });
        if (current) weekly.push(current);
        return weekly;
    }

    /* ── 2. Close-only ─────────────────────────────────────── */
    _convertToCloseOnly(daily) {
        return daily.map((d) => ({ time: d.time, value: d.close }));
    }

    /* ── 3. Heikin-Ashi ────────────────────────────────────── */
    _convertToHeikinAshi(daily) {
        if (!daily.length) return [];
        const out = [];
        let prevOpen = daily[0].open;
        let prevClose = daily[0].close;
        daily.forEach((d, i) => {
            const haClose = (d.open + d.high + d.low + d.close) / 4;
            const haOpen  = i === 0 ? (d.open + d.close) / 2 : (prevOpen + prevClose) / 2;
            const haHigh  = Math.max(d.high, haOpen, haClose);
            const haLow   = Math.min(d.low,  haOpen, haClose);
            out.push({ time: d.time, open: haOpen, high: haHigh, low: haLow, close: haClose });
            prevOpen = haOpen; prevClose = haClose;
        });
        return out;
    }

    /* ── 4. 3-Line Break ───────────────────────────────────── */
    _convertToThreeLineBreak(daily) {
        if (daily.length < 4) return [];
        const blocks = [];
        const history = [daily[0].close];
        let fakeTime = 1_600_000_000;
        for (let i = 1; i < daily.length; i++) {
            const close = daily[i].close;
            const last = blocks[blocks.length - 1] || { open: daily[0].close, close: daily[0].close };
            let spawn = false;
            const open = last.close;
            if (close > Math.max(last.open, last.close)) {
                spawn = true;
            } else if (close < Math.min(last.open, last.close)) {
                const lookback = history.slice(-3);
                if (close < Math.min(...lookback) || close > Math.max(...lookback)) spawn = true;
            }
            if (spawn) {
                blocks.push({
                    time: this._fakeTime(fakeTime++),
                    open, high: Math.max(open, close), low: Math.min(open, close), close,
                });
                history.push(close);
            }
        }
        return blocks;
    }

    /* ── 5. Renko (close-based) ────────────────────────────── */
    _convertToRenko(daily, brickSize) {
        if (!daily.length) return [];
        const bricks = [];
        let fakeTime = 1_700_000_000;
        let anchor = Math.round(daily[0].close / brickSize) * brickSize;
        daily.forEach((d) => {
            const diff = d.close - anchor;
            const n = Math.floor(Math.abs(diff) / brickSize);
            if (n > 0) {
                const dir = diff > 0 ? 1 : -1;
                for (let i = 0; i < n; i++) {
                    const next = anchor + dir * brickSize;
                    bricks.push({
                        time: this._fakeTime(fakeTime++),
                        open: anchor,
                        high: Math.max(anchor, next),
                        low:  Math.min(anchor, next),
                        close: next,
                    });
                    anchor = next;
                }
            }
        });
        return bricks;
    }

    /* ── 6. Point & Figure ─────────────────────────────────── */
    _convertToPointAndFigure(daily, boxSize, reversalBoxes) {
        if (!daily.length) return [];
        const cols = [];
        let anchor = Math.round(daily[0].close / boxSize) * boxSize;
        let isUp = true;
        let col = [anchor];
        let fakeTime = 1_800_000_000;

        const pushColumn = (upcol, arr) => {
            if (arr.length < 1) return;
            const open  = arr[0];
            const close = arr[arr.length - 1];
            cols.push({
                time: fakeTime++,
                open,
                high: Math.max(...arr),
                low:  Math.min(...arr),
                close,
                _up: upcol,
            });
        };

        daily.forEach((d) => {
            const c = d.close;
            if (isUp) {
                // extend up-column
                if (c >= anchor + boxSize) {
                    while (c >= anchor + boxSize) { anchor += boxSize; col.push(anchor); }
                }
                // reversal: price drops ≥ reversalBoxes × boxSize below column high
                else if (c <= anchor - reversalBoxes * boxSize) {
                    pushColumn(true, col);
                    isUp = false;
                    col = [anchor];
                    while (c <= anchor - boxSize) { anchor -= boxSize; col.push(anchor); }
                }
            } else {
                // extend down-column
                if (c <= anchor - boxSize) {
                    while (c <= anchor - boxSize) { anchor -= boxSize; col.push(anchor); }
                }
                // reversal: price rises ≥ reversalBoxes × boxSize above column low
                else if (c >= anchor + reversalBoxes * boxSize) {
                    pushColumn(false, col);
                    isUp = true;
                    col = [anchor];
                    while (c >= anchor + boxSize) { anchor += boxSize; col.push(anchor); }
                }
            }
        });
        if (col.length) pushColumn(isUp, col);
        // Drop private helper flag before returning
        return cols.map(({ _up, ...rest }) => rest);
    }
}
